import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { 
  MdEmail, 
  MdPhone, 
  MdPerson, 
  MdWork, 
  MdDescription,
  MdCheckCircle,
  MdHourglassEmpty,
  MdCancel,
  MdRefresh,
  MdSearch
} from 'react-icons/md';
import './Feedback.css';

const Feedback = () => {
  const { user } = useContext(AuthContext);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    filterSubmissions();
  }, [searchTerm, filterStatus, submissions]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/contact/submissions');
      if (response.data.success) {
        setSubmissions(response.data.data);
        setFilteredSubmissions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = [...submissions];

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(sub => sub.status === filterStatus);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(sub =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.service_interested && sub.service_interested.toLowerCase().includes(searchTerm.toLowerCase())) ||
        sub.project_details.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSubmissions(filtered);
  };

  const updateStatus = async (id, newStatus) => {
    setUpdatingStatus(id);
    try {
      const response = await axios.patch(`/api/contact/submissions/${id}/status`, {
        status: newStatus
      });

      if (response.data.success) {
        // Update local state
        setSubmissions(prev =>
          prev.map(sub =>
            sub.id === id ? { ...sub, status: newStatus } : sub
          )
        );
        
        if (selectedSubmission && selectedSubmission.id === id) {
          setSelectedSubmission({ ...selectedSubmission, status: newStatus });
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <MdHourglassEmpty />;
      case 'contacted':
        return <MdCheckCircle />;
      case 'in_progress':
        return <MdRefresh />;
      case 'completed':
        return <MdCheckCircle />;
      case 'rejected':
        return <MdCancel />;
      default:
        return <MdHourglassEmpty />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return '#3B82F6';
      case 'contacted':
        return '#10B981';
      case 'in_progress':
        return '#F59E0B';
      case 'completed':
        return '#059669';
      case 'rejected':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusCount = (status) => {
    if (status === 'all') return submissions.length;
    return submissions.filter(sub => sub.status === status).length;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content">
          <div className="access-denied">
            <h2>Access Denied</h2>
            <p>Only administrators can view contact submissions.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content feedback-main">
        <div className="feedback-page">
          {/* Header */}
          <div className="feedback-header">
            <div>
              <h1><MdEmail style={{ verticalAlign: 'middle', marginRight: 8 }} />Contact Submissions</h1>
              <p className="feedback-subtitle">
                Manage inquiries from JiruuTech website
              </p>
            </div>
            <button className="refresh-btn" onClick={fetchSubmissions} disabled={loading}>
              <MdRefresh className={loading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>

          {/* Stats */}
          <div className="feedback-stats">
            <div className="stat-card" onClick={() => setFilterStatus('all')}>
              <div className="stat-icon all">
                <MdEmail />
              </div>
              <div className="stat-info">
                <div className="stat-value">{getStatusCount('all')}</div>
                <div className="stat-label">Total Submissions</div>
              </div>
            </div>

            <div className="stat-card" onClick={() => setFilterStatus('new')}>
              <div className="stat-icon new">
                <MdHourglassEmpty />
              </div>
              <div className="stat-info">
                <div className="stat-value">{getStatusCount('new')}</div>
                <div className="stat-label">New</div>
              </div>
            </div>

            <div className="stat-card" onClick={() => setFilterStatus('contacted')}>
              <div className="stat-icon contacted">
                <MdCheckCircle />
              </div>
              <div className="stat-info">
                <div className="stat-value">{getStatusCount('contacted')}</div>
                <div className="stat-label">Contacted</div>
              </div>
            </div>

            <div className="stat-card" onClick={() => setFilterStatus('in_progress')}>
              <div className="stat-icon progress">
                <MdRefresh />
              </div>
              <div className="stat-info">
                <div className="stat-value">{getStatusCount('in_progress')}</div>
                <div className="stat-label">In Progress</div>
              </div>
            </div>

            <div className="stat-card" onClick={() => setFilterStatus('completed')}>
              <div className="stat-icon completed">
                <MdCheckCircle />
              </div>
              <div className="stat-info">
                <div className="stat-value">{getStatusCount('completed')}</div>
                <div className="stat-label">Completed</div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="feedback-filters">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, email, or service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              className="status-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Submissions List */}
          <div className="feedback-content">
            {loading ? (
              <div className="loading-state">
                <div className="spinner-large"></div>
                <p>Loading submissions...</p>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="empty-state">
                <MdEmail style={{ fontSize: 64, color: '#CBD5E1' }} />
                <h3>No submissions found</h3>
                <p>
                  {searchTerm || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Contact submissions from JiruuTech website will appear here'}
                </p>
              </div>
            ) : (
              <div className="submissions-grid">
                {filteredSubmissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="submission-card"
                    onClick={() => setSelectedSubmission(submission)}
                  >
                    <div className="submission-header">
                      <div className="submission-info">
                        <h3><MdPerson /> {submission.name}</h3>
                        <span className="submission-date">{formatDate(submission.created_at)}</span>
                      </div>
                      <div
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(submission.status) }}
                      >
                        {getStatusIcon(submission.status)}
                        {submission.status.replace('_', ' ')}
                      </div>
                    </div>

                    <div className="submission-details">
                      <div className="detail-row">
                        <MdEmail />
                        <span>{submission.email}</span>
                      </div>
                      {submission.phone && (
                        <div className="detail-row">
                          <MdPhone />
                          <span>{submission.phone}</span>
                        </div>
                      )}
                      {submission.service_interested && (
                        <div className="detail-row">
                          <MdWork />
                          <span>{submission.service_interested}</span>
                        </div>
                      )}
                    </div>

                    <div className="submission-preview">
                      <MdDescription />
                      <p>{submission.project_details.substring(0, 100)}...</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detail Modal */}
        {selectedSubmission && (
          <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Submission Details</h2>
                <button className="close-btn" onClick={() => setSelectedSubmission(null)}>×</button>
              </div>

              <div className="modal-body">
                <div className="detail-section">
                  <label><MdPerson /> Name</label>
                  <p>{selectedSubmission.name}</p>
                </div>

                <div className="detail-section">
                  <label><MdEmail /> Email</label>
                  <p>
                    <a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a>
                  </p>
                </div>

                {selectedSubmission.phone && (
                  <div className="detail-section">
                    <label><MdPhone /> Phone</label>
                    <p>
                      <a href={`tel:${selectedSubmission.phone}`}>{selectedSubmission.phone}</a>
                    </p>
                  </div>
                )}

                {selectedSubmission.service_interested && (
                  <div className="detail-section">
                    <label><MdWork /> Service Interested</label>
                    <p>{selectedSubmission.service_interested}</p>
                  </div>
                )}

                <div className="detail-section">
                  <label><MdDescription /> Project Details</label>
                  <p className="project-details">{selectedSubmission.project_details}</p>
                </div>

                <div className="detail-section">
                  <label>Submitted</label>
                  <p>{formatDate(selectedSubmission.created_at)}</p>
                </div>

                <div className="detail-section">
                  <label>Status</label>
                  <div className="status-actions">
                    {['new', 'contacted', 'in_progress', 'completed', 'rejected'].map((status) => (
                      <button
                        key={status}
                        className={`status-btn ${selectedSubmission.status === status ? 'active' : ''}`}
                        style={{
                          backgroundColor: selectedSubmission.status === status ? getStatusColor(status) : 'transparent',
                          color: selectedSubmission.status === status ? 'white' : getStatusColor(status),
                          borderColor: getStatusColor(status)
                        }}
                        onClick={() => updateStatus(selectedSubmission.id, status)}
                        disabled={updatingStatus === selectedSubmission.id}
                      >
                        {getStatusIcon(status)}
                        {status.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feedback;
